<template lang="pug">
.transaction-item
  .transaction-icon(:style="{ background: iconBgColor }")
    img(:src="iconSrc" :alt="title" :style="iconStyle")

  .transaction-content
    .transaction-title {{ title }}
    .transaction-description(v-if="description") {{ description }}

  .transaction-amount-date
    .transaction-amount(:style="{ color: amountColor }") {{ amountText }}
    .transaction-date {{ formattedDate }}
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import dayjs from 'dayjs';

export default defineComponent({
  name: 'TransactionItem',
  props: {
    transaction: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    // Transaction type configuration
    const transactionTypeConfig: Record<string, any> = {
      MONEY_ADDED: {
        icon: 'plus.svg',
        iconColor: '#0d9488',
        iconSize: 14,
        title: 'Added funds to wallet',
        isPositive: true,
      },
      MONEY_TRANSFERED: {
        icon: 'tray-arrow-up.svg',
        iconColor: '#F43300',
        iconSize: 10,
        title: (tx: any) => `Money sent to ${tx.toEntityName || 'User'}`,
        isPositive: false,
        iconRotate: false,
      },
      MONEY_RECEIVED: {
        icon: 'tray-arrow-up.svg',
        iconColor: '#0d9488',
        iconSize: 10,
        title: (tx: any) => `Money received from ${tx.fromEntityName || 'User'}`,
        isPositive: true,
        iconRotate: true,
      },
      MONEY_DONATED: {
        icon: 'share.svg',
        iconColor: '#225c6b',
        iconSize: 10,
        title: (tx: any) => `Donation to ${tx.causeName || 'Cause'}`,
        isPositive: false,
      },
    };

    const config = computed(() => {
      const type = props.transaction.transactionType;
      return transactionTypeConfig[type] || transactionTypeConfig.MONEY_ADDED;
    });

    const iconSrc = computed(() => new URL(`../../../assets/icons/${config.value.icon}`, import.meta.url).href);

    const iconStyle = computed(() => {
      const size = config.value.iconSize || 12;
      const styles: any = {
        width: `${size}px`,
        height: `${size}px`,
        filter: 'brightness(0) saturate(100%)',
      };
      if (config.value.iconRotate) {
        styles.transform = 'rotate(180deg)';
      }
      return styles;
    });


    const iconBgColor = computed(() => {
      // Convert hex to rgba with 10% opacity
      const hex = config.value.iconColor;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, 0.1)`;
    });

    const title = computed(() => {
      const titleValue = config.value.title;
      return typeof titleValue === 'function'
        ? titleValue(props.transaction)
        : titleValue;
    });

    const description = computed(() => {
      let desc = props.transaction.description;
      if (desc === 'Sticker Purchase' && props.transaction.stickerType) {
        desc = `${props.transaction.stickerType} Sticker`;
      }
      return desc;
    });

    const isPositive = computed(() => config.value.isPositive);

    const amountColor = computed(() => (isPositive.value ? '#0d9488' : '#EB1B1D'));

    const amountText = computed(() => {
      const sign = isPositive.value ? '+' : '-';
      const amount = Math.abs(props.transaction.amount).toFixed(2);
      return `${sign} $${amount}`;
    });

    const formattedDate = computed(() => {
      if (!props.transaction.createdAt) return '';
      return dayjs(props.transaction.createdAt).format('MMM D, YYYY');
    });

    return {
      iconSrc,
      iconStyle,
      iconBgColor,
      title,
      description,
      amountColor,
      amountText,
      formattedDate,
    };
  },
});
</script>

<style lang="scss" scoped>
.transaction-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
}

.transaction-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.transaction-content {
  flex: 1;
  min-width: 0;
}

.transaction-title {
  font-size: 12px;
  font-weight: 600;
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: #080808;
}

.transaction-description {
  font-size: 11px;
  font-weight: 500;
  color: #474E47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.transaction-amount-date {
  text-align: right;
  flex-shrink: 0;
}

.transaction-amount {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 4px 0;
}

.transaction-date {
  font-size: 11px;
  color: #474E47;
  margin: 0;
}
</style>
